import { PropTypes as AmktCommonPropTypes } from '@atlassian/amkt-common';
import ImmutablePropTypes from '@atlassian/react-immutable-proptypes';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import { HOME } from "../../../AddonDiscovery/config/discoverCollections";
import { getFilters } from "../../config/filters";
import FeaturedContentOverride from "../../records/FeaturedContentOverride";
import { chainablePropType } from "../../utils/chainablePropType";
import * as products from "../products";
import { filtersPropTypeFactory, unenhancedPropTypeFactory } from "./filtersPropTypes";
const highlightResult = ImmutablePropTypes.contains({
  matchLevel: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired
});
const hitImage = ImmutablePropTypes.contains({
  image: PropTypes.string,
  highRes: PropTypes.string,
  smallImage: PropTypes.string,
  smallHighResImage: PropTypes.string
});
const hitVendor = ImmutablePropTypes.contains({
  isTopVendor: PropTypes.bool.isRequired
});
const hitDistribution = ImmutablePropTypes.contains({
  bundled: PropTypes.bool.isRequired,
  activeInstalls: PropTypes.number,
  downloads: PropTypes.number.isRequired,
  deltaInstalls: PropTypes.number.isRequired
});
const hitVersion = ImmutablePropTypes.contains({
  compatibilities: ImmutablePropTypes.listOf(ImmutablePropTypes.contains({
    applicationKey: PropTypes.string.isRequired,
    applicationName: PropTypes.string.isRequired,
    hosting: PropTypes.string.isRequired
  })),
  remotable: PropTypes.bool.isRequired
});
const hitRatings = ImmutablePropTypes.contains({
  avgStars: PropTypes.number.isRequired,
  numRatings: PropTypes.number.isRequired
});
const hit = fromSearch => ImmutablePropTypes.recordOf({
  _highlightResult: AmktCommonPropTypes.requiredIf(ImmutablePropTypes.contains({
    name: highlightResult.isRequired,
    tokenizedName: ImmutablePropTypes.listOf(highlightResult).isRequired,
    tagLine: highlightResult,
    tokenizedTagLine: ImmutablePropTypes.listOf(highlightResult)
  }), () => fromSearch),
  addonId: AmktCommonPropTypes.requiredIf(PropTypes.number, () => fromSearch),
  addonKey: AmktCommonPropTypes.requiredIf(PropTypes.string, () => fromSearch),
  releaseDate: AmktCommonPropTypes.requiredIf(PropTypes.number, () => fromSearch),
  name: PropTypes.string.isRequired,
  tagLine: PropTypes.string,
  categories: AmktCommonPropTypes.requiredIf(ImmutablePropTypes.listOf(PropTypes.string), () => fromSearch),
  deployments: AmktCommonPropTypes.requiredIf(ImmutablePropTypes.listOf(PropTypes.string), () => fromSearch),
  documentType: AmktCommonPropTypes.requiredIf(PropTypes.string, () => fromSearch),
  logo: AmktCommonPropTypes.requiredIf(hitImage, () => fromSearch),
  vendor: AmktCommonPropTypes.requiredIf(hitVendor, () => fromSearch),
  distribution: AmktCommonPropTypes.requiredIf(hitDistribution, () => fromSearch),
  version: AmktCommonPropTypes.requiredIf(hitVersion, () => fromSearch),
  ratings: AmktCommonPropTypes.requiredIf(hitRatings, () => fromSearch)
});
const colSpan = ImmutablePropTypes.contains({
  xl: PropTypes.number,
  lg: PropTypes.number,
  md: PropTypes.number,
  sm: PropTypes.number,
  xs: PropTypes.number
});
const featuredContentOverride = PropTypes.instanceOf(FeaturedContentOverride);
const algoliaClientPropType = chainablePropType((props, propName, componentName) => props[propName].constructor.name !== 'AlgoliaClient' ? new Error("Invalid prop ".concat(propName, " supplied to ").concat(componentName, ". Validation failed.")) : null);
const CommonPropTypes = {
  algoliaClient: algoliaClientPropType,
  algoliaConfig: ImmutablePropTypes.contains({
    applicationId: PropTypes.string.isRequired,
    apiKey: PropTypes.string.isRequired
  }),
  colSpan: ImmutablePropTypes.contains({
    largeHits: colSpan,
    smallHits: colSpan
  }),
  contentOverrides: ImmutablePropTypes.listOf(featuredContentOverride),
  discoverCollectionsConfig: ImmutablePropTypes.mapOf(ImmutablePropTypes.list, PropTypes.oneOf(Map(products).toList().push(HOME).toArray())),
  featuredContentOverride,
  filters: ImmutablePropTypes.contains(filtersPropTypeFactory(getFilters()).toJS()),
  resolveAlgoliaQueryFilterOverrides: ImmutablePropTypes.mapOf(PropTypes.func, PropTypes.oneOf(getFilters().keySeq().toJS())),
  hit() {
    function checkType(isRequired, props) {
      const test = hit(props.fromSearch);
      for (var _len = arguments.length, restArgs = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        restArgs[_key - 2] = arguments[_key];
      }
      return (isRequired ? test.isRequired : test).apply(this, [props, ...restArgs]);
    }
    const chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);
    return chainedCheckType;
  },
  hitContext: ImmutablePropTypes.contains({
    hitHrefResolver: PropTypes.func,
    hitImageHrefResolver: PropTypes.func.isRequired,
    onHitClick: PropTypes.func.isRequired,
    onHitMount: PropTypes.func,
    onRatingsClick: PropTypes.func,
    hitGroup: PropTypes.string
  }),
  hitSize: PropTypes.oneOf(['large', 'small']),
  screenClass: PropTypes.oneOf(['xl', 'lg', 'md', 'sm', 'xs']),
  search: ImmutablePropTypes.contains({
    currentPage: PropTypes.number,
    dataError: PropTypes.bool.isRequired,
    dataErrorMessages: ImmutablePropTypes.list.isRequired,
    loadingFreshSearchResults: PropTypes.bool.isRequired,
    moreResultsDataError: PropTypes.bool.isRequired,
    moreResultsDataErrorMessages: ImmutablePropTypes.list.isRequired,
    searchResults: ImmutablePropTypes.contains({
      searchId: PropTypes.number,
      facets: ImmutablePropTypes.list,
      hits: ImmutablePropTypes.list,
      numberOfPages: PropTypes.number,
      numberOfHits: PropTypes.number
    }),
    initialized: PropTypes.bool.isRequired,
    searchInProgress: PropTypes.bool.isRequired
  }),
  transitions: ImmutablePropTypes.contains({
    transitionStarted: PropTypes.func,
    transitionCompleted: PropTypes.func
  }),
  unenhancedFilters: ImmutablePropTypes.contains(unenhancedPropTypeFactory(getFilters()).toJS())
};
export default CommonPropTypes;